import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Edit, Trash2, Users, Search, Upload, LayoutGrid, List, FileSpreadsheet, Printer } from "lucide-react";
import { toast } from "sonner";
import { PLAYER_POSITIONS, WEST_AFRICA_COUNTRIES } from "../lib/fcfa";
import { exportPlayersExcel } from "../lib/excel";
import { printPlayerCard } from "../lib/documents";
import { useAuth } from "../contexts/AuthContext";

const STATUS_BADGE = {
  actif: "bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30",
  blesse: "bg-[#F57F17]/10 text-[#F57F17] border-[#F57F17]/30",
  suspendu: "bg-[#C62828]/10 text-[#C62828] border-[#C62828]/30",
  transfere: "bg-[#616161]/10 text-[#616161] border-[#616161]/30",
};
const STATUS_LABEL = { actif: "Actif", blesse: "Blessé", suspendu: "Suspendu", transfere: "Transféré" };

const emptyForm = {
  first_name: "", last_name: "", team_id: "", date_of_birth: "", nationality: "",
  position: "", jersey_number: "", photo_url: "", status: "actif",
  contract_start: "", contract_end: "", phone: "", email: "", notes: "",
};

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTeam, setFilterTeam] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/players").then(({ data }) => setPlayers(data));
  useEffect(() => {
    load();
    api.get("/teams").then(({ data }) => setTeams(data));
  }, []);

  const filtered = useMemo(() => {
    return players.filter((p) => {
      if (filterStatus !== "all" && p.status !== filterStatus) return false;
      if (filterTeam !== "all" && p.team_id !== filterTeam) return false;
      if (search && !`${p.first_name} ${p.last_name}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [players, search, filterStatus, filterTeam]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      first_name: p.first_name || "", last_name: p.last_name || "",
      team_id: p.team_id || "", date_of_birth: p.date_of_birth || "",
      nationality: p.nationality || "", position: p.position || "",
      jersey_number: p.jersey_number ?? "", photo_url: p.photo_url || "",
      status: p.status || "actif", contract_start: p.contract_start || "",
      contract_end: p.contract_end || "", phone: p.phone || "",
      email: p.email || "", notes: p.notes || "",
    });
    setOpen(true);
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Photo téléchargée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      team_id: form.team_id || null,
      jersey_number: form.jersey_number ? parseInt(form.jersey_number, 10) : null,
      date_of_birth: form.date_of_birth || null,
      contract_start: form.contract_start || null,
      contract_end: form.contract_end || null,
    };
    try {
      if (editing) {
        await api.patch(`/players/${editing.id}`, payload);
        toast.success("Joueur modifié");
      } else {
        await api.post("/players", payload);
        toast.success("Joueur créé");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Supprimer ce joueur ?")) return;
    await api.delete(`/players/${id}`);
    toast.success("Supprimé");
    load();
  };

  const onPrintPlayer = async (p) => {
    const { data: stats } = await api.get(`/performances/aggregate/${p.id}`).catch(() => ({ data: null }));
    printPlayerCard(user.club, p, stats);
  };

  const photoSrc = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    const token = localStorage.getItem("asc_token");
    return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${token}`;
  };

  return (
    <div>
      <PageHeader
        overline="Effectif"
        title="Joueurs"
        description={`${filtered.length} joueur${filtered.length > 1 ? "s" : ""} affiché${filtered.length > 1 ? "s" : ""} sur ${players.length}.`}
        actions={
          <>
            <Button variant="outline" onClick={() => exportPlayersExcel(filtered)} data-testid="export-players-excel-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button onClick={openCreate} data-testid="create-player-btn" className="btn-club">
              <Plus className="w-4 h-4 mr-2" /> Nouveau joueur
            </Button>
          </>
        }
      />

      <div className="bg-white border border-[#E0E0E0] rounded-md p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#616161]" />
          <Input className="pl-9" placeholder="Rechercher un joueur..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="players-search-input" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="actif">Actifs</SelectItem>
            <SelectItem value="blesse">Blessés</SelectItem>
            <SelectItem value="suspendu">Suspendus</SelectItem>
            <SelectItem value="transfere">Transférés</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterTeam} onValueChange={setFilterTeam}>
          <SelectTrigger className="w-44" data-testid="filter-team"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes équipes</SelectItem>
            {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex border border-[#E0E0E0] rounded-md p-0.5">
          <Button size="sm" variant={view === "grid" ? "default" : "ghost"} onClick={() => setView("grid")}
            className={view === "grid" ? "btn-club" : ""} data-testid="view-grid-btn">
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button size="sm" variant={view === "table" ? "default" : "ghost"} onClick={() => setView("table")}
            className={view === "table" ? "btn-club" : ""} data-testid="view-table-btn">
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E0E0E0] rounded-md p-12 text-center">
          <Users className="w-10 h-10 text-[#C62828]/40 mx-auto mb-4" />
          <h3 className="font-display text-xl font-bold">Aucun joueur</h3>
          <p className="text-sm text-[#616161] mt-2">Ajoutez vos joueurs pour commencer.</p>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="players-grid">
          {filtered.map((p) => (
            <div key={p.id} className="group bg-white border border-[#E0E0E0] rounded-md overflow-hidden hover:border-[#C62828]/40 transition" data-testid={`player-card-${p.id}`}>
              <div className="aspect-square bg-gradient-to-br from-[#F4F5F7] to-[#E0E0E0] relative">
                {p.photo_url ? (
                  <img src={photoSrc(p.photo_url)} alt={p.last_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl font-display font-bold text-[#C62828]">
                    {(p.first_name?.[0] || "") + (p.last_name?.[0] || "")}
                  </div>
                )}
                {p.jersey_number !== null && p.jersey_number !== undefined && (
                  <span className="absolute top-2 left-2 w-8 h-8 rounded-md bg-[#9E1B1B] text-white font-display font-bold text-sm flex items-center justify-center">
                    {p.jersey_number}
                  </span>
                )}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition flex gap-1">
                  <button onClick={() => openEdit(p)} className="w-7 h-7 rounded bg-white/95 flex items-center justify-center" data-testid={`edit-player-${p.id}`}>
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(p.id)} className="w-7 h-7 rounded bg-white/95 flex items-center justify-center" data-testid={`delete-player-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-[#C62828]" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="font-display font-bold text-sm truncate">{p.first_name} {p.last_name}</p>
                <p className="text-xs text-[#616161] truncate">{p.position || "—"}{p.team_name ? ` · ${p.team_name}` : ""}</p>
                <Badge variant="outline" className={`mt-2 ${STATUS_BADGE[p.status]} border text-[10px]`}>{STATUS_LABEL[p.status]}</Badge>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="players-table">
              <thead className="bg-[#F4F5F7] text-[#616161] tracking-overline text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3">N°</th>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Poste</th>
                  <th className="text-left px-4 py-3">Équipe</th>
                  <th className="text-left px-4 py-3">Nationalité</th>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E0E0E0]">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-[#F4F5F7]/50" data-testid={`player-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono">{p.jersey_number ?? "—"}</td>
                    <td className="px-4 py-3 font-medium">{p.first_name} {p.last_name}</td>
                    <td className="px-4 py-3">{p.position || "—"}</td>
                    <td className="px-4 py-3">{p.team_name || "—"}</td>
                    <td className="px-4 py-3">{p.nationality || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`${STATUS_BADGE[p.status]} border text-[10px]`}>{STATUS_LABEL[p.status]}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)}><Trash2 className="w-4 h-4 text-[#C62828]" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="player-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier le joueur" : "Nouveau joueur"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-md bg-[#F4F5F7] border border-[#E0E0E0] overflow-hidden flex items-center justify-center">
                {form.photo_url ? (
                  <img src={photoSrc(form.photo_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Users className="w-6 h-6 text-[#616161]" />
                )}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="player-photo-input" />
                <Button type="button" variant="outline" disabled={uploading} asChild>
                  <span><Upload className="w-4 h-4 mr-2" /> {uploading ? "Envoi..." : "Photo"}</span>
                </Button>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom *</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="player-firstname" /></div>
              <div><Label>Nom *</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="player-lastname" /></div>
              <div><Label>N° maillot</Label><Input type="number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} data-testid="player-jersey" /></div>
              <div>
                <Label>Poste</Label>
                <Select value={form.position || "none"} onValueChange={(v) => setForm({ ...form, position: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="player-position"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {PLAYER_POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Équipe</Label>
                <Select value={form.team_id || "none"} onValueChange={(v) => setForm({ ...form, team_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="player-team"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sans équipe</SelectItem>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="player-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="blesse">Blessé</SelectItem>
                    <SelectItem value="suspendu">Suspendu</SelectItem>
                    <SelectItem value="transfere">Transféré</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nationalité</Label>
                <Select value={form.nationality || "none"} onValueChange={(v) => setForm({ ...form, nationality: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="player-nationality"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {WEST_AFRICA_COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Date de naissance</Label><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Début contrat</Label><Input type="date" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} /></div>
              <div><Label>Fin contrat</Label><Input type="date" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} /></div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="player-submit-btn">
                {editing ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
